import { DOCUMENT_READER_FINISHED_RUN_STATES } from '@commenda/api/commenda-ai/document-reader/constants';
import {
  DocumentReaderReExtractState,
  DocumentReaderRunStage,
  DocumentReaderRunState,
  DocumentReaderWriteBackState,
} from '@commenda/api/commenda-ai/document-reader/types';

export type DocumentReadingStepKind =
  | 'unzip'
  | 'checkFiles'
  | 'readPages'
  | 'understand'
  | 'sort';

export interface IDocumentReadingStep {
  kind: DocumentReadingStepKind;
  label: string;
  stages: readonly DocumentReaderRunStage[];
}

/** What the chat last saw of a run, to tell when it has just finished reading, re-reading or writing back. */
export interface IDocumentReadingRunSnapshot {
  runId: string;
  runState: DocumentReaderRunState | undefined;
  writeBackProposalId: string | null;
  writeBackState: DocumentReaderWriteBackState | null;
  reExtractState: DocumentReaderReExtractState | null;
}

export class DocumentReadingRunHelper {
  /** The run's stages grouped into the five steps a person sees. The estimate is never a step of its
   * own: no price is shown, so it reads as the end of checking the files. */
  static readonly STEPS: readonly IDocumentReadingStep[] = [
    {
      kind: 'unzip',
      label: 'Unzip',
      stages: [DocumentReaderRunStage.STORE, DocumentReaderRunStage.UNPACK],
    },
    {
      kind: 'checkFiles',
      label: 'Check files',
      stages: [
        DocumentReaderRunStage.CHECK_FILES,
        DocumentReaderRunStage.ESTIMATE,
      ],
    },
    {
      kind: 'readPages',
      label: 'Read pages',
      stages: [DocumentReaderRunStage.READ],
    },
    {
      kind: 'understand',
      label: 'Understand',
      stages: [
        DocumentReaderRunStage.FIND,
        DocumentReaderRunStage.CARD,
        DocumentReaderRunStage.CUT,
        DocumentReaderRunStage.EXTRACT,
        DocumentReaderRunStage.CHECK_VALUES,
      ],
    },
    {
      kind: 'sort',
      label: 'Sort by company',
      stages: [DocumentReaderRunStage.GROUP, DocumentReaderRunStage.MATCH],
    },
  ];

  /** The backend posts a finish message only after the state moves and the results are gathered, which
   * can take a while on a large run, so the chat re-reads after each of these waits until it shows. */
  static readonly FINISH_MESSAGE_RETRY_DELAYS_MS: readonly number[] = [
    2_000, 4_000, 8_000, 15_000, 30_000, 60_000,
  ];

  static isFinished(state: DocumentReaderRunState): boolean {
    return DOCUMENT_READER_FINISHED_RUN_STATES.includes(state);
  }

  /** The step a stage belongs to; null for DONE, which belongs to none. */
  static getStep(stage: DocumentReaderRunStage): IDocumentReadingStep | null {
    return (
      DocumentReadingRunHelper.STEPS.find((step) =>
        step.stages.includes(stage)
      ) ?? null
    );
  }

  static isWriteBackFinished(
    state: DocumentReaderWriteBackState | null
  ): boolean {
    return (
      state === DocumentReaderWriteBackState.DONE ||
      state === DocumentReaderWriteBackState.FAILED
    );
  }

  /** True when, between two looks at a loaded run, the run completed, a re-read ended or a write-back
   * ended: each posts a chat message. The first look counts only for a run started in this session, which
   * may finish before it is first read; one already finished when the chat opened does not re-read it. */
  static hasJustFinished(
    previous: Readonly<IDocumentReadingRunSnapshot> | null,
    current: Readonly<IDocumentReadingRunSnapshot>,
    isNewInSession: boolean
  ): boolean {
    if (previous?.runId !== current.runId || !previous.runState)
      return (
        isNewInSession && current.runState === DocumentReaderRunState.COMPLETED
      );
    const hasRunCompleted =
      previous.runState !== DocumentReaderRunState.COMPLETED &&
      current.runState === DocumentReaderRunState.COMPLETED;
    const hasReExtractEnded =
      previous.reExtractState === DocumentReaderReExtractState.RUNNING &&
      current.reExtractState !== null &&
      current.reExtractState !== DocumentReaderReExtractState.RUNNING;
    const wasSameWriteBackFinished =
      previous.writeBackProposalId === current.writeBackProposalId &&
      DocumentReadingRunHelper.isWriteBackFinished(previous.writeBackState);
    const hasWriteBackFinished =
      DocumentReadingRunHelper.isWriteBackFinished(current.writeBackState) &&
      !wasSameWriteBackFinished;
    return hasRunCompleted || hasReExtractEnded || hasWriteBackFinished;
  }

  static formatCount(value: number): string {
    return value.toLocaleString('en-US');
  }

  static formatCountOf(
    value: number,
    singular: string,
    plural: string
  ): string {
    return `${DocumentReadingRunHelper.formatCount(value)} ${value === 1 ? singular : plural}`;
  }
}
